import { useEffect, useRef, useCallback, useState } from 'react';
import {
  Bold, Italic, Underline, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote,
  Link, Image, Code, Minus,
  AlignLeft, AlignCenter, AlignRight,
  Undo, Redo, RemoveFormatting, X, Code2
} from 'lucide-react';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
}

interface ToolbarButton {
  icon: React.ReactNode;
  title: string;
  command?: string;
  arg?: string;
  action?: () => void;
  separator?: boolean;
}

interface LinkModalState {
  open: boolean;
  url: string;
  text: string;
  hasSelection: boolean;
  savedRange: Range | null;
}

interface ImageModalState {
  open: boolean;
  url: string;
  alt: string;
  savedRange: Range | null;
}

export function WysiwygEditor({ value, onChange, placeholder = 'Écrivez votre contenu...', minHeight = 400 }: WysiwygEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);

  const [htmlMode, setHtmlMode] = useState(false);
  const [linkModal, setLinkModal] = useState<LinkModalState>({
    open: false, url: 'https://', text: '', hasSelection: false, savedRange: null
  });
  const [imageModal, setImageModal] = useState<ImageModalState>({
    open: false, url: 'https://', alt: '', savedRange: null
  });

  const toggleHtmlMode = useCallback(() => {
    setHtmlMode(prev => {
      if (!prev) {
        return true;
      } else {
        if (editorRef.current) {
          editorRef.current.innerHTML = value;
        }
        return false;
      }
    });
  }, [value]);

  useEffect(() => {
    if (editorRef.current && !isUpdatingRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
  }, [value]);

  const saveSelection = useCallback((): Range | null => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      return sel.getRangeAt(0).cloneRange();
    }
    return null;
  }, []);

  const restoreSelection = useCallback((range: Range | null) => {
    if (!range) return;
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const exec = useCallback((command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    editorRef.current?.focus();
    handleInput();
  }, []);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isUpdatingRef.current = true;
      onChange(editorRef.current.innerHTML);
      isUpdatingRef.current = false;
    }
  }, [onChange]);

  const openLinkModal = useCallback(() => {
    const sel = window.getSelection();
    const selectedText = sel?.toString() || '';
    const savedRange = saveSelection();
    setLinkModal({
      open: true,
      url: 'https://',
      text: selectedText,
      hasSelection: selectedText.length > 0,
      savedRange
    });
  }, [saveSelection]);

  const confirmLink = useCallback(() => {
    const { url, text, hasSelection, savedRange } = linkModal;
    if (!url || url === 'https://') return;
    restoreSelection(savedRange);
    editorRef.current?.focus();
    if (hasSelection) {
      exec('createLink', url);
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const anchor = sel.anchorNode?.parentElement?.closest('a');
        if (anchor) {
          anchor.setAttribute('target', '_blank');
          anchor.setAttribute('rel', 'noopener noreferrer');
        }
      }
    } else {
      const linkText = text || url;
      exec('insertHTML', `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`);
    }
    setLinkModal(s => ({ ...s, open: false, savedRange: null }));
  }, [linkModal, restoreSelection, exec]);

  const openImageModal = useCallback(() => {
    const savedRange = saveSelection();
    setImageModal({ open: true, url: 'https://', alt: '', savedRange });
  }, [saveSelection]);

  const confirmImage = useCallback(() => {
    const { url, alt, savedRange } = imageModal;
    if (!url || url === 'https://') return;
    restoreSelection(savedRange);
    editorRef.current?.focus();
    exec('insertHTML', `<img src="${url}" alt="${alt}" style="max-width:100%;height:auto;" />`);
    setImageModal(s => ({ ...s, open: false, savedRange: null }));
  }, [imageModal, restoreSelection, exec]);

  const insertTable = useCallback(() => {
    const html = `<table style="width:100%;border-collapse:collapse;margin:16px 0;">
      <thead>
        <tr>
          <th style="border:1px solid #e5e7eb;padding:8px 12px;background:#f9fafb;text-align:left;">Colonne 1</th>
          <th style="border:1px solid #e5e7eb;padding:8px 12px;background:#f9fafb;text-align:left;">Colonne 2</th>
          <th style="border:1px solid #e5e7eb;padding:8px 12px;background:#f9fafb;text-align:left;">Colonne 3</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="border:1px solid #e5e7eb;padding:8px 12px;">Cellule</td>
          <td style="border:1px solid #e5e7eb;padding:8px 12px;">Cellule</td>
          <td style="border:1px solid #e5e7eb;padding:8px 12px;">Cellule</td>
        </tr>
      </tbody>
    </table>`;
    exec('insertHTML', html);
  }, [exec]);

  const toolbarGroups: ToolbarButton[][] = [
    [
      { icon: <Undo className="w-4 h-4" />, title: 'Annuler', command: 'undo' },
      { icon: <Redo className="w-4 h-4" />, title: 'Rétablir', command: 'redo' },
      { icon: <RemoveFormatting className="w-4 h-4" />, title: 'Supprimer la mise en forme', command: 'removeFormat' },
    ],
    [
      { icon: <Heading1 className="w-4 h-4" />, title: 'Titre 1', command: 'formatBlock', arg: 'h1' },
      { icon: <Heading2 className="w-4 h-4" />, title: 'Titre 2', command: 'formatBlock', arg: 'h2' },
      { icon: <Heading3 className="w-4 h-4" />, title: 'Titre 3', command: 'formatBlock', arg: 'h3' },
    ],
    [
      { icon: <Bold className="w-4 h-4" />, title: 'Gras', command: 'bold' },
      { icon: <Italic className="w-4 h-4" />, title: 'Italique', command: 'italic' },
      { icon: <Underline className="w-4 h-4" />, title: 'Souligné', command: 'underline' },
      { icon: <Strikethrough className="w-4 h-4" />, title: 'Barré', command: 'strikeThrough' },
      { icon: <Code className="w-4 h-4" />, title: 'Code inline', command: 'insertHTML', arg: '<code>code</code>' },
    ],
    [
      { icon: <AlignLeft className="w-4 h-4" />, title: 'Aligner à gauche', command: 'justifyLeft' },
      { icon: <AlignCenter className="w-4 h-4" />, title: 'Centrer', command: 'justifyCenter' },
      { icon: <AlignRight className="w-4 h-4" />, title: 'Aligner à droite', command: 'justifyRight' },
    ],
    [
      { icon: <List className="w-4 h-4" />, title: 'Liste à puces', command: 'insertUnorderedList' },
      { icon: <ListOrdered className="w-4 h-4" />, title: 'Liste numérotée', command: 'insertOrderedList' },
      { icon: <Quote className="w-4 h-4" />, title: 'Citation', command: 'formatBlock', arg: 'blockquote' },
      { icon: <Minus className="w-4 h-4" />, title: 'Séparateur horizontal', command: 'insertHorizontalRule' },
    ],
    [
      { icon: <Link className="w-4 h-4" />, title: 'Insérer un lien', action: openLinkModal },
      { icon: <Image className="w-4 h-4" />, title: 'Insérer une image', action: openImageModal },
    ],
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      exec('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') { e.preventDefault(); exec('bold'); }
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') { e.preventDefault(); exec('italic'); }
    if ((e.ctrlKey || e.metaKey) && e.key === 'u') { e.preventDefault(); exec('underline'); }
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
      <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1">
        {toolbarGroups.map((group, gi) => (
          <div key={gi} className={`flex items-center gap-0.5 ${htmlMode ? 'opacity-30 pointer-events-none' : ''}`}>
            {gi > 0 && <div className="w-px h-6 bg-gray-300 mx-1" />}
            {group.map((btn, bi) => (
              <button
                key={bi}
                type="button"
                title={btn.title}
                disabled={htmlMode}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (btn.action) {
                    btn.action();
                  } else if (btn.command) {
                    exec(btn.command, btn.arg);
                  }
                }}
                className="p-1.5 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors"
              >
                {btn.icon}
              </button>
            ))}
          </div>
        ))}
        <div className="flex items-center gap-0.5 ml-auto">
          <div className="w-px h-6 bg-gray-300 mx-1" />
          {!htmlMode && (
            <button
              type="button"
              title="Insérer un tableau"
              onMouseDown={(e) => { e.preventDefault(); insertTable(); }}
              className="px-2 py-1 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors text-xs font-medium"
            >
              Tableau
            </button>
          )}
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <button
            type="button"
            title={htmlMode ? 'Retour éditeur visuel' : 'Éditer le HTML'}
            onMouseDown={(e) => { e.preventDefault(); toggleHtmlMode(); }}
            className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors text-xs font-medium ${htmlMode ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'hover:bg-gray-200 text-gray-600 hover:text-gray-900'}`}
          >
            <Code2 className="w-3.5 h-3.5" />
            {htmlMode ? 'Visuel' : 'HTML'}
          </button>
        </div>
      </div>

      {htmlMode ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ minHeight, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 13 }}
          className="w-full p-4 focus:outline-none resize-y bg-gray-950 text-green-400 leading-relaxed"
          placeholder="<p>Écrivez votre HTML ici...</p>"
          spellCheck={false}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          style={{ minHeight }}
          data-placeholder={placeholder}
          className="p-4 focus:outline-none wysiwyg-content"
        />
      )}

      <style>{`
        .wysiwyg-content:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .wysiwyg-content h1 { font-size: 2em; font-weight: 700; margin: 0.67em 0; }
        .wysiwyg-content h2 { font-size: 1.5em; font-weight: 700; margin: 0.75em 0; }
        .wysiwyg-content h3 { font-size: 1.17em; font-weight: 600; margin: 0.83em 0; }
        .wysiwyg-content p { margin: 0.5em 0; }
        .wysiwyg-content ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
        .wysiwyg-content ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        .wysiwyg-content blockquote {
          border-left: 4px solid #d1d5db;
          margin: 1em 0;
          padding: 0.5em 1em;
          color: #6b7280;
          background: #f9fafb;
          border-radius: 0 4px 4px 0;
        }
        .wysiwyg-content code {
          background: #f3f4f6;
          padding: 0.125em 0.375em;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.875em;
          color: #1f2937;
        }
        .wysiwyg-content a { color: #2563eb; text-decoration: underline; }
        .wysiwyg-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5em 0; }
        .wysiwyg-content img { max-width: 100%; height: auto; border-radius: 4px; }
        .wysiwyg-content table { width: 100%; border-collapse: collapse; margin: 1em 0; }
        .wysiwyg-content th, .wysiwyg-content td { border: 1px solid #e5e7eb; padding: 8px 12px; }
        .wysiwyg-content th { background: #f9fafb; font-weight: 600; }
      `}</style>

      {linkModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onMouseDown={(e) => { if (e.target === e.currentTarget) setLinkModal(s => ({ ...s, open: false })); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Link className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">Insérer un lien</h3>
              </div>
              <button type="button" onClick={() => setLinkModal(s => ({ ...s, open: false }))} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">URL <span className="text-red-500">*</span></label>
                <input
                  type="url"
                  autoFocus
                  value={linkModal.url}
                  onChange={(e) => setLinkModal(s => ({ ...s, url: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmLink(); } if (e.key === 'Escape') setLinkModal(s => ({ ...s, open: false })); }}
                  placeholder="https://exemple.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {!linkModal.hasSelection && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Texte du lien</label>
                  <input
                    type="text"
                    value={linkModal.text}
                    onChange={(e) => setLinkModal(s => ({ ...s, text: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmLink(); } if (e.key === 'Escape') setLinkModal(s => ({ ...s, open: false })); }}
                    placeholder="Texte affiché (optionnel, utilise l'URL par défaut)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
              {linkModal.hasSelection && (
                <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  Le lien sera appliqué sur le texte sélectionné.
                </p>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setLinkModal(s => ({ ...s, open: false }))}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmLink}
                disabled={!linkModal.url || linkModal.url === 'https://'}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}

      {imageModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onMouseDown={(e) => { if (e.target === e.currentTarget) setImageModal(s => ({ ...s, open: false })); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Image className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">Insérer une image</h3>
              </div>
              <button type="button" onClick={() => setImageModal(s => ({ ...s, open: false }))} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">URL de l'image <span className="text-red-500">*</span></label>
                <input
                  type="url"
                  autoFocus
                  value={imageModal.url}
                  onChange={(e) => setImageModal(s => ({ ...s, url: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmImage(); } if (e.key === 'Escape') setImageModal(s => ({ ...s, open: false })); }}
                  placeholder="https://exemple.com/image.jpg"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Texte alternatif (alt)</label>
                <input
                  type="text"
                  value={imageModal.alt}
                  onChange={(e) => setImageModal(s => ({ ...s, alt: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmImage(); } if (e.key === 'Escape') setImageModal(s => ({ ...s, open: false })); }}
                  placeholder="Description de l'image pour l'accessibilité"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {imageModal.url && imageModal.url !== 'https://' && (
                <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center" style={{ minHeight: 80 }}>
                  <img
                    src={imageModal.url}
                    alt="Aperçu"
                    className="max-h-40 max-w-full object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setImageModal(s => ({ ...s, open: false }))}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmImage}
                disabled={!imageModal.url || imageModal.url === 'https://'}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
